import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _isNumber from 'lodash/isNumber'
import _isFunction from 'lodash/isFunction'
import { Map } from 'immutable'
import Link from 'components/Link'
import Button from 'components/Button'
import { ICON_TYPES } from 'components/Icon.v2'
import WatchAccess, {
  ACCESS_PREVIEW,
  ACCESS_FEATURE,
} from 'components/WatchAccess'
import WatchAccessAllowed from 'components/WatchAccessAllowed'
import WatchAccessDenied from 'components/WatchAccessDenied'
import { featureIsFree, isFeatureAllowedWithSubscription } from 'services/subscription'
import { getBoundActions } from 'actions'
import {
  upstreamContextOnClick as upstreamContextClick,
  WATCH_BUTTON,
  THUMBNAIL,
} from 'services/upstream-context'

export const WATCH_NODE_TYPE_FEATURE = 'WATCH_NODE_TYPE_FEATURE'
export const WATCH_NODE_TYPE_PREVIEW = 'WATCH_NODE_TYPE_PREVIEW' // UNUSED
export const WATCH_RENDER_TYPE_OVERLAY_LINK = 'WATCH_RENDER_TYPE_OVERLAY_LINK'
export const WATCH_RENDER_TYPE_BUTTON = 'WATCH_RENDER_TYPE_BUTTON'

export function createWatchUrl (auth, feature, preview, url) {
  const previewId = preview.get('id')
  const featureId = feature.get('id')
  const featureOfferingIsFree = featureIsFree(
    feature.getIn(['offerings', 'availability']),
  )
  const jwt = auth.get('jwt')
  if (_isNumber(featureId) && featureId > 0 && (jwt || featureOfferingIsFree)) {
    return `${url + getQuerySeparator(url)}fullplayer=feature`
  }
  if (_isNumber(previewId) && previewId > 0) {
    return `${url + getQuerySeparator(url)}fullplayer=preview`
  }
  return url
}

function getQuerySeparator (url) {
  if (url.indexOf('?') === -1) {
    return '?'
  }
  return '&'
}

function getClassName (inputClassName) {
  return ['watch'].concat(inputClassName || []).join(' ')
}

class Watch extends Component {
  handleClick = (event) => {
    const { props } = this
    const {
      onClickWatch,
      upstreamContext,
      fullContext,
      setUpstreamContext,
    } = props

    const context = fullContext || upstreamContext
    let error
    try {
      this.watchNowGaEvent()

      if (_isFunction(upstreamContextClick)) {
        upstreamContextClick(event, { upstreamContext: context, setUpstreamContext })
      }
    } catch (e) {
      // Save tracking error to allow onClickWatch to run first
      error = e
    }
    try {
      if (_isFunction(onClickWatch)) {
        onClickWatch()
      }
    } catch (e) {
      // Prefer to send interaction error
      error = e
    }
    if (error) {
      throw error
    }
  }

  watchNowGaEvent = () => {
    const { props } = this
    const { upstreamContext = Map(), setDefaultGa4Event } = props

    // GA4 events
    const playClickEventData = Map.isMap(upstreamContext) ? upstreamContext.get('playClickEventData', Map()) : Map()
    if (Map.isMap(playClickEventData) && playClickEventData.size > 0) {
      setDefaultGa4Event(playClickEventData)
    }
  }

  renderAccessAllowedFeatureText = () => {
    const {
      staticText, feature, auth, text,
    } = this.props
    const featureOfferingIsFree = featureIsFree(feature.getIn(['offerings', 'availability']))
    const userIsEntitled = isFeatureAllowedWithSubscription(feature, auth)
    let watchText = staticText.get('watchNow')

    if (text) {
      watchText = text
    } else if (featureOfferingIsFree && !userIsEntitled) {
      watchText = staticText.get('watchNowForFree')
    }

    return watchText
  }

  renderAccessAllowedFeature = () => {
    const { props, handleClick } = this
    const {
      url,
      className,
      style,
      children,
      renderType,
      buttonClass,
      buttonType,
      iconType,
      title,
    } = props
    const query = { fullplayer: 'feature' }

    if (renderType === WATCH_RENDER_TYPE_OVERLAY_LINK) {
      return (
        <Link
          to={url}
          query={query}
          data-element={THUMBNAIL}
          onClick={handleClick}
          className={getClassName(className)}
          style={style}
          title={title}
        >
          {children}
        </Link>
      )
    } if (renderType === WATCH_RENDER_TYPE_BUTTON) {
      return (
        <Button
          style={style}
          type={buttonType}
          icon={iconType}
          url={url}
          query={query}
          element={WATCH_BUTTON}
          onClick={handleClick}
          className={buttonClass}
        >
          {this.renderAccessAllowedFeatureText()}
        </Button>
      )
    }
    return null
  }

  renderAccessAllowedPreview = () => {
    const {
      props,
      handleClick,
    } = this
    const {
      url,
      className,
      style,
      children,
      renderType,
      buttonClass,
      buttonType,
      staticText,
      asShare,
      title,
    } = props
    const query = { fullplayer: 'preview' }
    if (renderType === WATCH_RENDER_TYPE_OVERLAY_LINK) {
      return (
        asShare
          ? <Button
            type={buttonType}
            onClick={handleClick}
            className={getClassName(className)}
            style={style}
            title={title}
          >
            {children}
          </Button>
          : <Link
            to={url}
            query={query}
            onClick={handleClick}
            className={getClassName(className)}
            style={style}
            title={title}
          >
            {children}
          </Link>
      )
    } if (renderType === WATCH_RENDER_TYPE_BUTTON) {
      return (
        <Button
          type={buttonType}
          icon={ICON_TYPES.CHEVRON_LEFT}
          url={asShare ? '' : url}
          query={asShare ? {} : query}
          element={WATCH_BUTTON}
          onClick={handleClick}
          className={buttonClass}
        >
          {staticText.get('preview')}
        </Button>
      )
    }
    return null
  }

  /**
   * If we have no access attempt to send the visitor to a player page and
   * let the player page messaging sort out what they should do next. If
   * there is no feature or preview send them to a detail page.
   */
  renderAccessDenied = () => {
    const { props, handleClick } = this
    const {
      url,
      className,
      style,
      children,
      renderType,
      buttonClass,
      buttonType,
      staticText,
      iconType,
      deniedText,
      title,
      excludeDeniedDefault,
    } = props
    const query = {}

    if (excludeDeniedDefault) {
      return null
    }

    if (renderType === WATCH_RENDER_TYPE_OVERLAY_LINK) {
      return (
        <Link
          to={url}
          query={query}
          data-element={THUMBNAIL}
          onClick={handleClick}
          className={getClassName(className)}
          style={style}
          title={title}
        >
          {children}
        </Link>
      )
    } if (renderType === WATCH_RENDER_TYPE_BUTTON) {
      return (
        <Button
          scrollToTop
          type={buttonType}
          url={url}
          query={query}
          icon={iconType}
          element={WATCH_BUTTON}
          onClick={handleClick}
          className={buttonClass}
        >
          {deniedText || staticText.get('viewDetails')}
        </Button>
      )
    }
    return null
  }

  render () {
    const {
      props,
      renderAccessAllowedFeature,
      renderAccessAllowedPreview,
      renderAccessDenied,
    } = this

    const {
      auth, preview, feature, type, forceAccess, isFirstEpisode,
    } = props

    return (
      <WatchAccess
        auth={auth}
        preview={preview}
        feature={feature}
        type={type}
        forceAccess={forceAccess}
        isFirstEpisode={isFirstEpisode}
      >
        <WatchAccessAllowed access={ACCESS_FEATURE}>
          {renderAccessAllowedFeature()}
        </WatchAccessAllowed>
        <WatchAccessAllowed access={ACCESS_PREVIEW}>
          {renderAccessAllowedPreview()}
        </WatchAccessAllowed>
        <WatchAccessDenied>
          {renderAccessDenied()}
        </WatchAccessDenied>
      </WatchAccess>
    )
  }
}

Watch.propTypes = {
  url: PropTypes.string.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  feature: ImmutablePropTypes.map.isRequired,
  preview: ImmutablePropTypes.map.isRequired,
  style: PropTypes.object,
  className: PropTypes.array,
  renderType: PropTypes.string.isRequired,
  buttonClass: PropTypes.string,
  buttonType: PropTypes.string,
  iconType: PropTypes.string,
  staticText: ImmutablePropTypes.map.isRequired,
  upstreamContext: ImmutablePropTypes.map,
  onClickWatch: PropTypes.func,
  asShare: PropTypes.bool,
  text: PropTypes.string,
  forceAccess: PropTypes.oneOf([
    ACCESS_FEATURE,
    ACCESS_PREVIEW,
  ]),
  title: PropTypes.string,
  excludeDeniedDefault: PropTypes.bool,
}

Watch.defaultProps = {
  upstreamContext: Map(),
}

const MemoizedWatch = React.memo(Watch)
export default connect(
  (state, props) => {
    const { upstreamContext } = props
    let id
    if (upstreamContext) {
      id = upstreamContext.get('videoId') || upstreamContext.get('seriesId', null)
    }
    const reduxContext = state.upstreamContext.get('data')
    let fullContext

    if (upstreamContext && reduxContext && (reduxContext.get('contentId') === id)) {
      const source = reduxContext.get('source')
      fullContext = upstreamContext.merge({
        source,
        score: reduxContext.get('score'),
        videoTasteSegment: reduxContext.get('videoTasteSegment'),
      })
    }

    return {
      fullContext,
      staticText: state.staticText.getIn(['data', 'watch', 'data']),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setUpstreamContext: actions.upstreamContext.setUpstreamContext,
      setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
    }
  },
)(MemoizedWatch)
